import { API_BASE_URL } from "../config/api";
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import {
  CONTACT_ROLE_OPTIONS,
  areAddressesEqual,
  cloneAddress,
  createEmptyCustomerForm,
  createEmptyContact,
  ensurePhonePrefix,
  mapCustomerToForm,
  sanitizeCustomerPayload
} from "../utils/customerForm";
import { getContactNumberWarning, getGstinWarning } from "../utils/formValidation";
import { fetchLocationByPincode, normalizePincode } from "../utils/locationApi";

const inputClassName =
  "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-4 focus:ring-sky-100";
const warningClassName = "mt-1 text-xs text-red-500";

function EditCustomer() {
  const role = localStorage.getItem("role") || "";
  const isAccountsView = role === "accounts";
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(createEmptyCustomerForm);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [useBillingForWorkAddress, setUseBillingForWorkAddress] = useState(false);
  const [locationStatus, setLocationStatus] = useState({
    billingAddress: { loading: false, message: "", tone: "idle" },
    address: { loading: false, message: "", tone: "idle" }
  });
  const phoneWarning = getContactNumberWarning(form.phone);
  const gstWarning = getGstinWarning(form.gstNumber);
  const isSezSupply = String(form.gstType || "").trim().toLowerCase() === "sez_supply";

  const [groups, setGroups] = useState([]);
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(API_BASE_URL + "/api/customer-groups");
        setGroups(res.data);
      } catch (err) {
        console.error("Failed to load customer groups", err);
      }
    };
    fetchGroups();
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchCustomer = async () => {
      try {
        const response = await axios.get(API_BASE_URL + `/api/customers/${id}`);
        if (isMounted) {
          const nextForm = mapCustomerToForm(response.data);
          setForm(nextForm);
          setUseBillingForWorkAddress(
            areAddressesEqual(nextForm.billingAddress, nextForm.address)
          );
        }
      } catch (error) {
        if (isMounted) {
          alert("Failed to load customer data");
        }
      } finally {
        if (isMounted) {
          setFetching(false);
        }
      }
    };

    fetchCustomer();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === "phone"
        ? ensurePhonePrefix(value)
        : name === "gstType"
          ? value
          : value,
      ...(name === "gstType" && value !== "sez_supply" ? { lutRefNo: "" } : {})
    }));
  };

  const handleAddressChange = (section, event) => {
    const { name, value } = event.target;
    const nextValue = name === "pincode" ? normalizePincode(value) : value;

    setForm((current) => {
      const nextSection = {
        ...current[section],
        [name]: nextValue
      };

      return {
        ...current,
        [section]: nextSection,
        ...(section === "billingAddress" && useBillingForWorkAddress
          ? { address: cloneAddress(nextSection) }
          : {})
      };
    });

    if (name === "pincode") {
      setLocationStatus((current) => ({
        ...current,
        [section]:
          nextValue.length === 6
            ? current[section]
            : { loading: false, message: "", tone: "idle" }
      }));
    }
  };

  const handlePincodeBlur = async (section) => {
    const pincode = normalizePincode(form[section]?.pincode);

    if (!pincode) {
      setLocationStatus((current) => ({
        ...current,
        [section]: { loading: false, message: "", tone: "idle" }
      }));
      return;
    }

    if (pincode.length !== 6) {
      setLocationStatus((current) => ({
        ...current,
        [section]: { loading: false, message: "Enter a valid 6-digit pincode.", tone: "error" }
      }));
      return;
    }

    setLocationStatus((current) => ({
      ...current,
      [section]: { loading: true, message: "Looking up city and state...", tone: "loading" }
    }));

    try {
      const location = await fetchLocationByPincode(pincode);

      setForm((current) => {
        const nextSection = {
          ...current[section],
          city: location.city || current[section].city,
          state: location.state || current[section].state
        };

        return {
          ...current,
          [section]: nextSection,
          ...(section === "billingAddress" && useBillingForWorkAddress
            ? { address: cloneAddress(nextSection) }
            : {})
        };
      });

      setLocationStatus((current) => ({
        ...current,
        [section]: {
          loading: false,
          message: "City and state updated from pincode.",
          tone: "success"
        }
      }));
    } catch (error) {
      setLocationStatus((current) => ({
        ...current,
        [section]: {
          loading: false,
          message: error.response?.data?.message || error.message || "Unable to fetch location.",
          tone: "error"
        }
      }));
    }
  };

  const getLocationMessageClassName = (tone) => {
    if (tone === "error") {
      return warningClassName;
    }

    if (tone === "success") {
      return "mt-1 text-xs text-emerald-600";
    }

    return "mt-1 text-xs text-slate-500";
  };

  const handleAdditionalChange = (index, field, value) => {
    setForm((current) => ({
      ...current,
      additionalContacts: current.additionalContacts.map((contact, contactIndex) => {
        if (contactIndex !== index) {
          return field === "isPrimary"
            ? { ...contact, isPrimary: false }
            : contact;
        }

        return {
          ...contact,
          [field]:
            field === "mobileNumber" || field === "whatsappNumber"
              ? ensurePhonePrefix(value)
              : field === "isPrimary"
                ? Boolean(value)
                : value,
          isPrimary: field === "isPrimary" ? Boolean(value) : contact.isPrimary
        };
      })
    }));
  };

  const addContact = () => {
    setForm((current) => ({
      ...current,
      additionalContacts: [
        ...current.additionalContacts,
        createEmptyContact(!current.additionalContacts.some((contact) => contact.isPrimary))
      ]
    }));
  };

  const removeContact = (index) => {
    setForm((current) => ({
      ...current,
      additionalContacts:
        current.additionalContacts.length === 1
          ? [createEmptyContact(true)]
          : current.additionalContacts
              .filter((_, contactIndex) => contactIndex !== index)
              .map((contact, contactIndex) => ({
                ...contact,
                isPrimary: contactIndex === 0
              }))
    }));
  };

  const handleUseBillingForWorkAddressChange = (event) => {
    const checked = event.target.checked;
    setUseBillingForWorkAddress(checked);

    if (checked) {
      setForm((current) => ({
        ...current,
        address: cloneAddress(current.billingAddress)
      }));

      setLocationStatus((current) => ({
        ...current,
        address: { ...current.billingAddress }
      }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      setLoading(true);
      let groupId = form.groupId;

      if (showNewGroupInput && newGroupName.trim()) {
        const groupRes = await axios.post(API_BASE_URL + "/api/customer-groups", {
          groupName: newGroupName.trim()
        });
        groupId = groupRes.data._id;
      }

      const payload = {
        ...sanitizeCustomerPayload(form),
        groupId: groupId || null
      };

      const response = await axios.put(API_BASE_URL + `/api/customers/${id}`, payload);

      if ((payload.gstNumber || "") !== (response.data?.gstNumber || "")) {
        throw new Error("Customer update did not persist GST number.");
      }

      alert("Customer updated successfully.");
      navigate(isAccountsView ? "/accounts/customers" : "/customers");
    } catch (error) {
      alert(error.response?.data?.message || error.message || "Error updating customer");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <div className="p-6">Loading customer...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 px-4 py-6 sm:px-6">
      <div className="mx-auto max-w-5xl">
        <section className="rounded-[28px] border border-slate-200 bg-[linear-gradient(135deg,_rgba(255,255,255,1),_rgba(248,250,252,1),_rgba(239,246,255,0.95))] p-6 shadow-sm">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-sky-700">
            Customer Management
          </p>
          <h1 className="mt-2 text-3xl font-bold text-slate-900">Edit Customer</h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">
            Update the customer record without carrying extra unused profile fields.
          </p>
        </section>

        <form onSubmit={handleSubmit} className="mt-6 space-y-6">
          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900">Primary Details</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Customer Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Enter customer name"
                  required
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Contact Person</label>
                <input
                  name="contactPerson"
                  value={form.contactPerson}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Primary contact person"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Phone / Telephone</label>
                <input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Mobile, landline, or telephone number"
                  required
                />
                {phoneWarning ? <p className={warningClassName}>{phoneWarning}</p> : null}
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Email address"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">GST Type</label>
                <select
                  name="gstType"
                  value={form.gstType}
                  onChange={handleChange}
                  className={inputClassName}
                >
                  <option value="cgst_sgst">GST</option>
                  <option value="igst">IGST</option>
                  <option value="sez_supply">SEZ SUPPLY</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">GST No</label>
                <input
                  name="gstNumber"
                  value={form.gstNumber}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="GST number"
                />
                {gstWarning ? <p className={warningClassName}>{gstWarning}</p> : null}
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Supplier Code</label>
                <input
                  name="supplierCode"
                  value={form.supplierCode || ""}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="Supplier code"
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Google Maps Location Link</label>
                <input
                  name="googleMapsLink"
                  value={form.googleMapsLink || ""}
                  onChange={handleChange}
                  className={inputClassName}
                  placeholder="E.g., https://goo.gl/maps/... or https://maps.google.com/..."
                />
              </div>

              {isSezSupply ? (
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">LUT Ref No</label>
                  <input
                    name="lutRefNo"
                    value={form.lutRefNo}
                    onChange={handleChange}
                    className={inputClassName}
                    placeholder="Enter LUT reference number"
                  />
                </div>
              ) : null}

              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Customer Group</label>
                <select
                  name="groupId"
                  value={showNewGroupInput ? "__new__" : (form.groupId || "")}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === "__new__") {
                      setShowNewGroupInput(true);
                    } else {
                      setShowNewGroupInput(false);
                      setForm(curr => ({ ...curr, groupId: val }));
                    }
                  }}
                  className={inputClassName}
                >
                  <option value="">None (Ungrouped)</option>
                  {groups.map((g) => (
                    <option key={g._id} value={g._id}>
                      {g.groupName}
                    </option>
                  ))}
                  <option value="__new__">+ Create New Group...</option>
                </select>
              </div>

              {showNewGroupInput ? (
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-slate-700">New Group Name</label>
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    className={inputClassName}
                    placeholder="Enter new group name"
                    required
                  />
                </div>
              ) : null}
            </div>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900">Billing Address</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Street</label>
                <input
                  name="street"
                  value={form.billingAddress.street}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Pincode</label>
                <input
                  name="pincode"
                  value={form.billingAddress.pincode}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  onBlur={() => handlePincodeBlur("billingAddress")}
                  className={inputClassName}
                  placeholder="Pincode"
                />
                {locationStatus.billingAddress.message ? (
                  <p className={getLocationMessageClassName(locationStatus.billingAddress.tone)}>
                    {locationStatus.billingAddress.message}
                  </p>
                ) : null}
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">City</label>
                <input
                  name="city"
                  value={form.billingAddress.city}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="City"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">State</label>
                <input
                  name="state"
                  value={form.billingAddress.state}
                  onChange={(event) => handleAddressChange("billingAddress", event)}
                  className={inputClassName}
                  placeholder="State"
                />
              </div>
            </div>

            <label className="mt-5 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700">
              <input
                type="checkbox"
                checked={useBillingForWorkAddress}
                onChange={handleUseBillingForWorkAddressChange}
                className="h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
              />
              Use the same address for work / delivery address
            </label>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900">Work / Delivery Address</h2>
            <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-2 block text-sm font-semibold text-slate-700">Street</label>
                <input
                  name="street"
                  value={form.address.street}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">Pincode</label>
                <input
                  name="pincode"
                  value={form.address.pincode}
                  onChange={(event) => handleAddressChange("address", event)}
                  onBlur={() => handlePincodeBlur("address")}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="Pincode"
                />
                {locationStatus.address.message ? (
                  <p className={getLocationMessageClassName(locationStatus.address.tone)}>
                    {locationStatus.address.message}
                  </p>
                ) : null}
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">City</label>
                <input
                  name="city"
                  value={form.address.city}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="City"
                />
              </div>

              <div>
                <label className="mb-2 block text-sm font-semibold text-slate-700">State</label>
                <input
                  name="state"
                  value={form.address.state}
                  onChange={(event) => handleAddressChange("address", event)}
                  disabled={useBillingForWorkAddress}
                  className={inputClassName}
                  placeholder="State"
                />
              </div>
            </div>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-bold text-slate-900">Additional Contacts</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Save backup contact details for service coordination.
                </p>
              </div>
              <button
                type="button"
                onClick={addContact}
                className="rounded-2xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-700"
              >
                + Add Contact
              </button>
            </div>

            <div className="mt-5 space-y-4">
              {form.additionalContacts.map((contact, index) => (
                <div
                  key={`contact-${index}`}
                  className="rounded-3xl border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
                    <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Role</label>
                        <select
                          value={contact.role}
                          onChange={(event) => handleAdditionalChange(index, "role", event.target.value)}
                          className={inputClassName}
                        >
                          {CONTACT_ROLE_OPTIONS.map((roleOption) => (
                            <option key={roleOption} value={roleOption}>
                              {roleOption}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Contact Person Name</label>
                        <input
                          placeholder="Contact person name"
                          value={contact.name}
                          onChange={(event) => handleAdditionalChange(index, "name", event.target.value)}
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Mobile Number</label>
                        <input
                          placeholder="Mobile number"
                          value={contact.mobileNumber}
                          onChange={(event) => handleAdditionalChange(index, "mobileNumber", event.target.value)}
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Email Address</label>
                        <input
                          type="email"
                          placeholder="Email address"
                          value={contact.email}
                          onChange={(event) => handleAdditionalChange(index, "email", event.target.value)}
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">WhatsApp Number</label>
                        <input
                          placeholder="WhatsApp number"
                          value={contact.whatsappNumber}
                          onChange={(event) => handleAdditionalChange(index, "whatsappNumber", event.target.value)}
                          className={inputClassName}
                        />
                      </div>
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Remarks</label>
                        <input
                          placeholder="Optional remarks"
                          value={contact.remarks}
                          onChange={(event) => handleAdditionalChange(index, "remarks", event.target.value)}
                          className={inputClassName}
                        />
                      </div>
                    </div>

                    <div className="flex shrink-0 flex-col gap-3">
                      <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700">
                        <input
                          type="radio"
                          name="primary-contact"
                          checked={Boolean(contact.isPrimary)}
                          onChange={() =>
                            setForm((current) => ({
                              ...current,
                              additionalContacts: current.additionalContacts.map((item, itemIndex) => ({
                                ...item,
                                isPrimary: itemIndex === index
                              }))
                            }))
                          }
                          className="h-4 w-4 border-slate-300 text-sky-600"
                        />
                        Primary Contact
                      </label>
                      <button
                        type="button"
                        onClick={() => removeContact(index)}
                        className="rounded-2xl bg-red-100 px-4 py-3 text-sm font-semibold text-red-600 transition hover:bg-red-200"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-bold text-slate-900">Additional Info</h2>
            <p className="mt-1 text-sm text-slate-500">
              Keep short service notes or customer-specific instructions here.
            </p>
            <textarea
              name="additionalInfo"
              value={form.additionalInfo}
              onChange={handleChange}
              className={`${inputClassName} mt-5 min-h-32 resize-y`}
              placeholder="Any notes you want the team to remember"
            />
          </section>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className={`rounded-2xl px-6 py-3 text-sm font-semibold text-white transition ${
                loading ? "bg-slate-400" : "bg-sky-600 hover:bg-sky-700"
              }`}
            >
              {loading ? "Saving..." : "Update Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditCustomer;


